import { useMemo, useState } from "react";
import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon, PlusIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";

export interface CategoryOption {
  id: string;
  name: string;
}

/** Stands in for the "create a new category" row; never a real category. */
const CREATE_ID = "__create__";

/**
 * A searchable category picker that holds the chosen id itself, for rows that are not part of a
 * react-hook-form form (the shared ComboBoxInput is tied to one). Search ignores spaces and case, so
 * "headrestfront" finds "Headrest Front RH".
 *
 * With onCreate set, the list always ends with a row to create a new category, named after whatever
 * was typed, for when the right category does not exist yet.
 */
export function CategoryPicker({
  categories,
  value,
  onChange,
  onCreate,
  placeholder = "Choose a category",
  className,
  ariaLabel = "Part category",
}: {
  categories: CategoryOption[];
  value: string | null;
  onChange: (id: string | null) => void;
  onCreate?: (name: string) => void;
  placeholder?: string;
  className?: string;
  ariaLabel?: string;
}) {
  const [query, setQuery] = useState("");

  const selected = useMemo(() => categories.find((c) => c.id === value) ?? null, [categories, value]);

  const filtered = useMemo(() => {
    const term = query.toLowerCase().replace(/\s+/g, "");
    if (!term) return categories.slice(0, 100);
    return categories.filter((c) => c.name.toLowerCase().replace(/\s+/g, "").includes(term)).slice(0, 100);
  }, [categories, query]);

  const typed = query.trim();
  const exact = categories.some((c) => c.name.toLowerCase() === typed.toLowerCase());

  const choose = (c: CategoryOption | null) => {
    if (c?.id === CREATE_ID) {
      onCreate?.(typed);
      return;
    }
    onChange(c?.id ?? null);
  };

  return (
    <Combobox value={selected} onChange={choose} onClose={() => setQuery("")}>
      <div className={clsx("relative", className)}>
        <ComboboxInput
          aria-label={ariaLabel}
          className="w-full rounded border border-gray-300 bg-white py-1.5 pl-3 pr-8 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-600"
          displayValue={(c: CategoryOption | null) => c?.name ?? ""}
          placeholder={placeholder}
          onChange={(e) => setQuery(e.target.value)}
        />
        <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2">
          <ChevronUpDownIcon className="h-4 w-4 text-gray-400" aria-hidden />
        </ComboboxButton>
        <ComboboxOptions
          anchor="bottom start"
          className="z-40 max-h-60 w-[var(--input-width)] min-w-[16rem] overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 empty:invisible"
        >
          {filtered.length === 0 && !onCreate && (
            <div className="px-3 py-2 text-gray-500">No category matches "{query}".</div>
          )}

          {filtered.map((c) => (
            <ComboboxOption
              key={c.id}
              value={c}
              className="group relative cursor-default select-none py-1.5 pl-8 pr-3 text-gray-900 data-[focus]:bg-orange-600 data-[focus]:text-white"
            >
              <CheckIcon className="invisible absolute left-2 top-2 h-4 w-4 group-data-[selected]:visible" aria-hidden />
              <span className="block truncate">{c.name}</span>
            </ComboboxOption>
          ))}

          {onCreate && !exact && (
            <ComboboxOption
              value={{ id: CREATE_ID, name: typed }}
              className="relative cursor-default select-none border-t border-gray-100 py-2 pl-8 pr-3 font-medium text-orange-700 data-[focus]:bg-orange-600 data-[focus]:text-white"
            >
              <PlusIcon className="absolute left-2 top-2.5 h-4 w-4" aria-hidden />
              {typed ? `Create "${typed}" as a new category` : "Create a new category"}
            </ComboboxOption>
          )}
        </ComboboxOptions>
      </div>
    </Combobox>
  );
}
